<template>
  <transition duration="300" name="fade" appear>
    <component
      v-if="!isPageLoading"
      class="absolute w-100"
      :is="dynamicComponent"
      :page="responseData"
      :key="$route.fullPath"
    ></component>
    <b-container v-else fluid class="page">
      <loading></loading>
    </b-container>
  </transition>
</template>

<script>
import find from "lodash/find";

// @ is an alias to /src
import { getWagtailPageByPath } from "@/api";
import NotFound from "@/views/NotFound.vue";
import Loading from "@/components/Loading.vue";

export default {
  name: "WagtailPageHandler",
  components: {
    RealHomePage: () => import("@/views/Home.vue"),
    ProjectIndexPage: () => import("@/views/Projects.vue"),
    ProjectPage: () => import("@/views/Project.vue"),
    MemberIndexPage: () => import("@/views/Members.vue"),
    MemberPage: () => import("@/views/Member.vue"),
    NewsIndexPage: () => import("@/views/News.vue"),
    NewsPage: () => import("@/views/Article.vue"),
    EventIndexPage: () => import("@/views/Events.vue"),
    EventPage: () => import("@/views/Event.vue"),
    PublicationIndexPage: () => import("@/views/Publications.vue"),
    PartnerIndexPage: () => import("@/views/Partners.vue"),
    FlexPage: () => import("@/views/Flex.vue"),
    AboutPage: () => import("@/views/AboutUs.vue"),
    SoftwareIndexPage: () => import("@/views/SoftwareList.vue"),
    SoftwarePage: () => import("@/views/Software.vue"),
    Loading,
  },
  data() {
    return {
      dynamicComponent: null,
      responseData: null,
      title: "",
      seoTitle: "",
      description: "",
      isPageLoading: false,
    };
  },
  metaInfo() {
    return {
      title: this.title,
      titleTemplate(titleChunk) {
        return this.seoTitle ? this.seoTitle : `${titleChunk}`;
      },
      meta: [{ name: "description", content: this.description }],
    };
  },
  beforeRouteEnter(to, from, next) {
    // called before the route that renders this component is confirmed.
    // does NOT have access to `this` component instance,
    // because it has not been created yet when this guard is called!
    getWagtailPageByPath(to.fullPath)
      .then((response) => {
        // console.log(response.data);
        // catch 302/200 response, render matching Vue component
        // pass page data from response to component
        next((vm) => {
          // access to component instance via `vm`
          vm.dynamicComponent = vm.getPageComponent(response.data.meta.type);
          vm.responseData = response.data;
          vm.setMetaData(response.data);
        });
      })
      .catch((error) => {
        // catch 404 response, page doesn't exist
        // retain request path but render NotFound component instead
        // TODO: find a way to actually return a 404 response

        next((vm) => {
          // access to component instance via `vm`
          vm.dynamicComponent = NotFound;
          vm.$router.replace("/not-found");
          vm.responseData = null;
        });
      });
  },
  beforeRouteUpdate(to, from, next) {
    this.isPageLoading = true;
    // called when the route that renders this component has changed,
    // but this component is reused in the new route.
    // has access to `this` component instance.

    // TODO: clean up
    // Most of this code is duplicated from the beforeRouteEnter() guard
    // but that guard isn't run when the route changes, only on initial load.
    getWagtailPageByPath(to.fullPath)
      .then((response) => {
        // console.log(response);
        this.dynamicComponent = this.getPageComponent(response.data.meta.type);
        this.responseData = response.data;
        this.setMetaData(response.data);

        this.isPageLoading = false;

        next();
      })
      .catch((error) => {
        this.dynamicComponent = NotFound;
        this.responseData = null;

        this.isPageLoading = false;

        next();
      });
  },
  methods: {
    getPageComponent(pageType) {
      // return registered component that's name matches our Wagtail page type
      // strip `pages.` from the Wagtail page type
      return find(
        this.$options.components,
        (obj) => obj.name === pageType.replace("pages.", "")
      );
    },
    setMetaData(page) {
      this.title = page.title;
      this.seoTitle = page.meta.seo_title;
      this.description = page.meta.search_description;
    },
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
